import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, X } from 'lucide-react';
import { FormField } from '../types/form';
import { isMultiRowOptions } from '../types/guards';
import CustomDatePicker from './CustomDatePicker';
import CustomFileUpload from './CustomFileUpload';

interface MultiRowFieldProps {
  field: FormField;
  value: any[][];
  onChange: (value: any[][]) => void;
  error?: string;
  errors?: { [key: string]: string };
  disabled?: boolean;
}

interface ColumnConfig {
  id: string;
  label: string;
  type: 'text' | 'number' | 'phone' | 'email' | 'dropdown' | 'date' | 'file' | 'checkbox';
  options?: string[];
  knowledge?: string;
  required?: boolean;
  placeholder?: string;
}

export const MultiRowField: React.FC<MultiRowFieldProps> = ({
  field,
  value = [],
  onChange,
  error,
  errors = {},
  disabled = false,
}) => {
  const multiRowOptions = field.options && isMultiRowOptions(field.options) ? field.options : null;
  const columns: ColumnConfig[] = React.useMemo(() => multiRowOptions?.columns || [], [multiRowOptions?.columns]);
  const allowAddRows = multiRowOptions?.allowAddRows !== false;
  const initialRowCount = multiRowOptions?.initialRowCount || 3;
  const rowLabels = multiRowOptions?.rowLabels || [];
  
  // Initialize with the specified number of rows
  React.useEffect(() => {
    if (value.length === 0 && columns.length > 0) {
      const initialRows = Array(initialRowCount).fill(null).map(() => columns.map(() => ''));
      onChange(initialRows);
    }
  }, [value.length, columns, onChange, initialRowCount]);
  
  const addRow = () => {
    const newRow = columns.map(() => '');
    onChange([...value, newRow]);
  };

  const removeRow = (rowIndex: number) => {
    // Don't allow removing the last row when users can't add rows
    if (!allowAddRows && value.length <= 1) {
      return;
    }
    const newValue = value.filter((_, index) => index !== rowIndex);
    onChange(newValue);
  };

  const updateCell = (rowIndex: number, colIndex: number, cellValue: any) => {
    const newValue = [...value];
    if (!newValue[rowIndex]) {
      newValue[rowIndex] = columns.map(() => '');
    }
    newValue[rowIndex][colIndex] = cellValue;
    onChange(newValue);
  };

  const renderCell = (column: ColumnConfig, rowIndex: number, colIndex: number) => {
    const cellValue = value[rowIndex]?.[colIndex] || '';
    const cellError = errors[`${field.id}-${rowIndex}-${colIndex}`];
    const hasError = !!cellError;
    
    switch (column.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div>
            <Input
              type={column.type === 'email' ? 'email' : column.type === 'phone' ? 'tel' : 'text'}
              placeholder={column.placeholder || column.label}
              value={cellValue}
              onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
              disabled={disabled}
              className={`h-8 ${hasError ? 'border-red-500' : ''}`}
            />
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
      
      case 'number':
        return (
          <div>
            <Input
              type="number"
              placeholder={column.placeholder || column.label}
              value={cellValue}
              onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
              disabled={disabled}
              className={`h-8 ${hasError ? 'border-red-500' : ''}`}
            />
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
      
      
      case 'dropdown':
        return (
          <div>
            <Select
              value={cellValue}
              onValueChange={(val) => updateCell(rowIndex, colIndex, val)}
              disabled={disabled}
            >
              <SelectTrigger className={`h-8 ${hasError ? 'border-red-500' : ''}`}>
                <SelectValue placeholder={`Select ${column.label}`} />
              </SelectTrigger>
              <SelectContent>
                {column.options && column.options.map((option, optionIndex) => (
                  <SelectItem key={optionIndex} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
      
      
      case 'date':
        return (
          <div>
            <CustomDatePicker
              value={cellValue}
              onChange={(date) => updateCell(rowIndex, colIndex, date)}
              disabled={disabled}
            />
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
      
      case 'file':
        return (
          <div>
            <CustomFileUpload
              onChange={(file) => updateCell(rowIndex, colIndex, file)}
              disabled={disabled}
            />
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
      
      case 'checkbox':
        // If column has options, render as multiple checkboxes
        if (column.options && column.options.length > 0) {
          const selectedValues = Array.isArray(cellValue) ? cellValue : [];
          return (
            <div className="space-y-1">
              {column.options.map((option, optionIndex) => (
                <div key={optionIndex} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`${rowIndex}-${colIndex}-${optionIndex}`}
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      const newSelectedValues = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter(v => v !== option);
                      updateCell(rowIndex, colIndex, newSelectedValues);
                    }}
                    disabled={disabled}
                    className="h-3 w-3 text-muted-foreground focus:ring-ring border-input rounded"
                  />
                  <label 
                    htmlFor={`${rowIndex}-${colIndex}-${optionIndex}`}
                    className="text-sm cursor-pointer"
                  >
                    {option}
                  </label>
                </div>
              ))}
              {cellError && (
                <p className="text-red-500 text-xs mt-1">{cellError}</p>
              )}
            </div>
          );
        }
        
        // If no options, render as single checkbox
        return (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={cellValue === true || cellValue === 'true'}
              onChange={(e) => updateCell(rowIndex, colIndex, e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-muted-foreground focus:ring-ring border-input rounded"
            />
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
      
      default:
        return (
          <div>
            <Input
              type="text"
              placeholder={column.placeholder || column.label}
              value={cellValue}
              onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
              disabled={disabled}
              className={`h-8 ${hasError ? 'border-red-500' : ''}`}
            />
            {cellError && (
              <p className="text-red-500 text-xs mt-1">{cellError}</p>
            )}
          </div>
        );
    }
  };

  if (columns.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
        No columns configured for this multi-row field
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="relative">
          <table className="w-full border border-border rounded-lg">
            <thead>
              <tr className="bg-muted">
                {rowLabels.length > 0 && (
                  <th className="px-3 py-2 text-left text-sm font-medium text-foreground border-b border-r">
                    Row
                  </th>
                )}
                {columns.map((column) => (
                  <th key={column.id} className="px-3 py-2 text-left text-sm font-medium text-foreground border-b">
                    {column.label}
                    {column.required && <span className="text-red-500 ml-1">*</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {value.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b group relative hover:bg-muted/50">
                  {rowLabels.length > 0 && (
                    <td className="px-3 py-2 border-r text-sm font-medium text-muted-foreground bg-muted">
                      {rowLabels[rowIndex] || `Row ${rowIndex + 1}`}
                    </td>
                  )}
                  {columns.map((column, colIndex) => (
                    <td key={`${rowIndex}-${colIndex}`} className="px-3 py-2 border-r last:border-r-0">
                      {renderCell(column, rowIndex, colIndex)}
                    </td>
                  ))}
                  
                  {/* Notion-style hover badge on top right */}
                  {allowAddRows && value.length > 1 && (
                    <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeRow(rowIndex)}
                        disabled={disabled}
                        className="h-5 w-5 p-0 rounded-full shadow-md text-white bg-red-500 hover:bg-red-600 disabled:opacity-50"
                      >
                        <X className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {allowAddRows && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          disabled={disabled}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Row
        </Button>
      )}
      
      {error && (
        <div className="text-sm text-red-500 mt-1">{error}</div>
      )}
    </div>
  );
};